import { ROTA_APIS } from "@/api_framework/api_config";
import { IRotaOverbookingRequestsFilters, IRotaOverbookingRequestsResponse } from "@/api_framework/api_modals/Rota";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import { setLoading } from "@/app_redux/reducers/loading_reducer";
import { setOverbookingRequests } from "@/app_redux/reducers/slices/auth/rota_slice";
import axios, { AxiosResponse } from "axios";
import AxiosCurlirize from "axios-curlirize";
import { useCallback, useRef } from "react";
import toast from "react-hot-toast";
import { useConfiguration } from "../../useConfiguration";
import { useCurlLogger } from "@/app/hooks/useCurlLogger";
import { useAPIUtils } from "../../useAPIUtils";

AxiosCurlirize(axios, () => {});

export const useRotaOverbookingRequestsAPI = () => {
  const configuration = useConfiguration();
  const dispatch = useAppDispatch();
  const { sendCurl } = useCurlLogger();
  const { captureError } = useAPIUtils();
  const latestRequestRef = useRef(0);

  const execute = useCallback(
    async (filters: IRotaOverbookingRequestsFilters) => {
      const requestId = latestRequestRef.current + 1;
      latestRequestRef.current = requestId;

      dispatch(setLoading({ key: "isOverbookingRequestsInProgress", status: true }));
      dispatch(
        setOverbookingRequests({
          data: null,
          totalCount: 0,
        }),
      );
      try {
        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
        const params = new URLSearchParams();

        if (filters?.clinic_ids) {
          const clinicId = Array.isArray(filters.clinic_ids) ? filters.clinic_ids[0] : filters.clinic_ids;
          if (clinicId) {
            params.set("clinic_ids", clinicId);
          }
        }
        if (filters?.service_type) {
          params.set("service_type", filters.service_type);
        }
        if (filters?.from_date) {
          params.set("from_date", filters.from_date);
        }
        if (filters?.to_date) {
          params.set("to_date", filters.to_date);
        }
        if (filters?.page) {
          params.set("page", `${filters.page}`);
        }
        if (filters?.page_size) {
          params.set("page_size", `${filters.page_size}`);
        }

        const res: AxiosResponse<IRotaOverbookingRequestsResponse> = await axios.get(
          ROTA_APIS?.ROTA_OVERBOOKING_REQUESTS?.baseURL ?? "",
          {
            params,
            headers: {
              Authorization: `${accessToken}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (requestId !== latestRequestRef.current) return;

        if (res.data.status === true) {
          const responseData = res.data.data;
          const nestedRequests =
            responseData && !Array.isArray(responseData) ? (responseData.requests ?? []) : [];
          const requestList = res.data.requests ?? nestedRequests ?? (Array.isArray(responseData) ? responseData : []);
          const nestedTotalCount =
            responseData && !Array.isArray(responseData)
              ? responseData.total_count ?? responseData.total_requests ?? responseData.count ?? responseData.total
              : undefined;
          const totalCount = res.data.total_count ?? res.data.total_requests ?? res.data.count ?? res.data.total ?? nestedTotalCount;
          dispatch(
            setOverbookingRequests({
              data: requestList,
              totalCount: totalCount ?? requestList.length,
            }),
          );
        } else {
          dispatch(
            setOverbookingRequests({
              data: [],
              totalCount: 0,
            }),
          );
        }
      } catch (error) {
        if (requestId !== latestRequestRef.current) return;

        if (axios.isAxiosError(error)) {
          sendCurl?.(error.config?.["curlCommand"]);
        } else {
          toast.error("Server Error: " + (error as Error).message);
        }
      } finally {
        if (requestId === latestRequestRef.current) {
          dispatch(setLoading({ key: "isOverbookingRequestsInProgress", status: false }));
        }
      }
    },
    [captureError, configuration, dispatch, sendCurl],
  );

  return execute;
};
